import * as React from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Hash, LayoutTemplate, Minus, PencilRuler, Plus, Redo2, RefreshCw, Save, StickyNote, Table2, Type, Undo2, Upload, BarChart3, LineChart, PieChart, Grid3x3, Heading, LayoutGrid, AreaChart } from 'lucide-react';
import type { ColumnTypes, DashboardRender, DashboardSpec, LayoutCell, Widget, WidgetType } from '@shared/dashboard-spec';
import { dashboardSpecSchema } from '@shared/dashboard-spec';
import type { Localised } from '@shared/types';
import { pickLocale } from '@shared/types';
import { api, ApiError, qk } from '@client/lib/api';
import type { AdminItem } from '@client/lib/admin-api';
import { useDataset, useDatasets, useTemplates, useDatasetVersionById, type DatasetVersion, type DatasetColumn } from '@client/lib/dataset-api';
import { starterSpec, addWidgetToSpec, duplicateWidgetInSpec, removeWidgetFromSpec, WIDGET_META, type FieldGuess } from '@client/lib/spec-templates';
import { seriesColorMap } from '@client/lib/dashboard-format';
import { Button } from '@client/components/ui/button';
import { Dialog } from '@client/components/ui/overlay';
import { Field, Input, Select, Textarea } from '@client/components/ui/input';
import { Spinner, EmptyState } from '@client/components/ui/feedback';
import { Badge } from '@client/components/ui/badge';
import { Segmented } from '@client/components/ui/segmented';
import { Tooltip } from '@client/components/ui/tooltip';
import { useToast } from '@client/components/ui/toast';
import { BuilderCanvas } from '@client/components/builder/canvas';
import { Inspector } from '@client/components/builder/inspector';
import type { BuilderCtx } from '@client/components/builder/editors';
import type { DrillResult } from '@client/components/viewer/dashboard-viewer';
import { cn } from '@client/lib/utils';

const DashboardViewer = React.lazy(() => import('@client/components/viewer/dashboard-viewer').then((m) => ({ default: m.DashboardViewer })));

export const Route = createFileRoute('/admin/dashboards/$itemId')({ component: BuilderPage });

interface ItemResp { item: AdminItem & { roomId: string }; versions: Array<{ id: string; versionNo: number; datasetVersionId: string | null; renderStatus: string; renderError: string | null; asOf: string | null; changeNote: Localised | null; publishedAt: string | null; createdAt: string }> }

type Mode = 'edit' | 'preview' | 'json';

const PALETTE: Array<{ type: WidgetType; icon: React.ComponentType<{ className?: string }> }> = [
  { type: 'kpi', icon: Hash }, { type: 'bar', icon: BarChart3 }, { type: 'stacked_bar', icon: BarChart3 }, { type: 'line', icon: LineChart }, { type: 'area', icon: AreaChart },
  { type: 'donut', icon: PieChart }, { type: 'treemap', icon: LayoutGrid }, { type: 'matrix', icon: Grid3x3 }, { type: 'table', icon: Table2 },
  { type: 'header', icon: Heading }, { type: 'text', icon: Type }, { type: 'note', icon: StickyNote }, { type: 'divider', icon: Minus },
];

/** Undo/redo history; edits within 800 ms coalesce into one step so typing does not create a step per keystroke. */
function useHistory() {
  const [h, setH] = React.useState<{ past: DashboardSpec[]; present: DashboardSpec | null; future: DashboardSpec[] }>({ past: [], present: null, future: [] });
  const last = React.useRef(0);
  const set = React.useCallback((next: DashboardSpec) => {
    const now = Date.now();
    const coalesce = now - last.current < 800;
    last.current = now;
    setH((s) => ({ past: s.present && !coalesce ? [...s.past.slice(-99), s.present] : s.past, present: next, future: [] }));
  }, []);
  const reset = React.useCallback((next: DashboardSpec | null) => { last.current = 0; setH({ past: [], present: next, future: [] }); }, []);
  const undo = React.useCallback(() => { last.current = 0; setH((s) => (s.past.length && s.present ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1]!, future: [s.present, ...s.future] } : s)); }, []);
  const redo = React.useCallback(() => { last.current = 0; setH((s) => (s.future.length && s.present ? { past: [...s.past, s.present], present: s.future[0]!, future: s.future.slice(1) } : s)); }, []);
  return { spec: h.present, set, reset, undo, redo, canUndo: h.past.length > 0, canRedo: h.future.length > 0 };
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => { const t = window.setTimeout(() => setV(value), ms); return () => window.clearTimeout(t); }, [value, ms]);
  return v;
}

function guessDim(columns: DatasetColumn[], dateField: string): string | undefined {
  const strings = columns.filter((c) => c.type === 'string' && c.name !== dateField);
  return strings.find((c) => /categor|type|tipo|class|group|gruppo|segment|kind|area|department|reparto/i.test(c.name))?.name ?? strings[0]?.name;
}

function BuilderPage() {
  const { itemId } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const item = useQuery({ queryKey: qk.admin('item', itemId), queryFn: () => api<ItemResp>(`/api/admin/items/${itemId}`) });
  const datasets = useDatasets();
  const templates = useTemplates();

  const [datasetVersionId, setDatasetVersionId] = React.useState<string | null>(null);
  const hist = useHistory();
  const spec = hist.spec;
  const [initialised, setInitialised] = React.useState(false);
  const [savedJson, setSavedJson] = React.useState('');
  const [mode, setMode] = React.useState<Mode>('edit');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [previewState, setPreviewState] = React.useState<Record<string, string>>({});
  const [jsonText, setJsonText] = React.useState('');
  const [jsonError, setJsonError] = React.useState<string | null>(null);
  const [templateOpen, setTemplateOpen] = React.useState(false);

  const loadSpec = React.useCallback((raw: unknown) => {
    const parsed = dashboardSpecSchema.safeParse(raw);
    const s = parsed.success ? parsed.data : (raw as DashboardSpec);
    hist.reset(s);
    setSavedJson(JSON.stringify(s));
    setJsonText(JSON.stringify(s, null, 2));
    setJsonError(null);
  }, [hist]);

  // Initialise from the latest version's spec + dataset binding, once.
  React.useEffect(() => {
    if (initialised || !item.data) return;
    const latest = item.data.versions[0];
    if (latest) {
      setDatasetVersionId(latest.datasetVersionId);
      api<{ version: { spec: unknown } }>(`/api/admin/dashboards/versions/${latest.id}`).then((r) => loadSpec(r.version.spec)).catch(() => undefined);
    }
    setInitialised(true);
  }, [item.data, initialised, loadSpec]);

  const bound = useDatasetVersionById(datasetVersionId);
  const dataset = bound.data?.dataset;
  const version = bound.data?.version;
  const tables = React.useMemo(() => version?.schema?.tables.map((t) => t.name) ?? [], [version]);
  const tableSchema = version?.schema?.tables.find((t) => t.name === spec?.table) ?? version?.schema?.tables[0];
  const columns = React.useMemo(() => tableSchema?.columns ?? [], [tableSchema]);
  const columnTypes = React.useMemo(() => {
    const out: ColumnTypes = {};
    for (const c of columns) out[c.name] = c.type;
    for (const [k, cfg] of Object.entries(dataset?.columnConfig ?? {})) if (cfg.type) out[k] = cfg.type;
    return out;
  }, [columns, dataset]);
  const ctx: BuilderCtx | null = spec && dataset && version ? { datasetId: dataset.id, versionId: version.id, table: spec.table, tables, columns, columnTypes, dateField: spec.date_field, controls: spec.controls } : null;
  const guess: FieldGuess | null = spec ? { table: spec.table, dateField: spec.date_field, amountField: columns.find((c) => c.type === 'currency')?.name ?? columns.find((c) => c.type === 'number')?.name ?? 'amount', dimField: guessDim(columns, spec.date_field) } : null;

  const debounced = useDebounced(spec, 350);
  const validation = React.useMemo(() => (debounced ? dashboardSpecSchema.safeParse(debounced) : null), [debounced]);
  const issues = validation && !validation.success ? validation.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`) : [];
  const preview = useQuery({
    queryKey: qk.admin('dash-preview', itemId, JSON.stringify(debounced), datasetVersionId ?? '', JSON.stringify(previewState)),
    queryFn: () => api<{ render: DashboardRender; columns: ColumnTypes }>('/api/admin/dashboards/preview', { body: { spec: debounced, dataset_version_id: datasetVersionId, state: previewState } }),
    enabled: !!debounced && !!datasetVersionId && !!validation?.success,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const render = preview.data?.render;
  const data = React.useMemo(() => (render ? Object.values(render.states)[0] ?? {} : {}), [render]);
  const colors = React.useMemo(() => (render ? seriesColorMap(render) : {}), [render]);

  const commit = React.useCallback((next: DashboardSpec) => { hist.set(next); setJsonText(JSON.stringify(next, null, 2)); setJsonError(null); }, [hist]);
  const dirty = !!spec && JSON.stringify(spec) !== savedJson;

  const drill = React.useCallback<(w: string, s: string, k: Record<string, string>, q?: string) => Promise<DrillResult>>((widget_id, state, keys, q) => {
    const st: Record<string, string> = {};
    for (const part of state.split('&').filter(Boolean)) { const i = part.indexOf('='); if (i > 0) st[part.slice(0, i)] = part.slice(i + 1); }
    return api<DrillResult>('/api/admin/dashboards/preview/drill', { body: { spec, dataset_version_id: datasetVersionId, widget_id, state: st, keys, q } });
  }, [spec, datasetVersionId]);

  const save = useMutation({
    mutationFn: (publish: boolean) => api<{ version: { id: string; renderStatus: string; renderError: string | null } }>(`/api/admin/dashboards/items/${itemId}/versions`, { body: { spec, dataset_version_id: datasetVersionId, change_note: spec?.subtitle ?? null } }).then(async (res) => {
      if (res.version.renderStatus === 'failed') throw new ApiError(400, 'render_failed', res.version.renderError ?? undefined);
      if (publish) await api(`/api/admin/items/${itemId}/publish`, { method: 'POST' });
      return res;
    }),
    onSuccess: (_res, publish) => { setSavedJson(JSON.stringify(spec)); qc.invalidateQueries({ queryKey: qk.admin('item', itemId) }); qc.invalidateQueries({ queryKey: qk.admin('dashboards-list') }); toast.push({ tone: 'good', title: publish ? 'Published' : 'Version saved' }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not save', body: e instanceof ApiError ? (e.reason ?? e.code) : String(e) }),
  });

  const saveTemplate = useMutation({
    mutationFn: (name: string) => api('/api/admin/dashboards/templates', { body: { name, description: spec?.subtitle?.en ?? null, spec } }),
    onSuccess: () => { setTemplateOpen(false); qc.invalidateQueries({ queryKey: qk.admin('templates') }); toast.push({ tone: 'good', title: 'Template saved' }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not save template', body: e instanceof ApiError ? e.code : String(e) }),
  });

  // Keyboard: undo/redo, delete selected widget (never while typing in a field).
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) hist.redo(); else hist.undo(); }
      else if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); hist.redo(); }
      else if ((e.key === 'Backspace' || e.key === 'Delete') && selectedId && spec && mode === 'edit') { e.preventDefault(); commit(removeWidgetFromSpec(spec, selectedId)); setSelectedId(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hist, selectedId, spec, mode, commit]);

  React.useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const addWidget = (type: WidgetType) => {
    if (!spec || !guess) return;
    const res = addWidgetToSpec(spec, type, guess);
    commit(res.spec);
    setSelectedId(res.id);
    setMode('edit');
  };
  const removeWidget = (id: string) => { if (!spec) return; commit(removeWidgetFromSpec(spec, id)); if (selectedId === id) setSelectedId(null); };
  const duplicateWidget = (id: string) => { if (!spec) return; const res = duplicateWidgetInSpec(spec, id); if (res) { commit(res.spec); setSelectedId(res.id); } };
  const onWidget = (w: Widget) => { if (!spec) return; commit({ ...spec, widgets: spec.widgets.map((x) => (x.id === w.id ? w : x)) }); };
  const onLayout = (cells: LayoutCell[]) => { if (!spec) return; commit({ ...spec, layout: cells }); };
  const onSpecPatch = (patch: Partial<DashboardSpec>) => { if (!spec) return; commit({ ...spec, ...patch }); };
  const validateJson = (text: string) => {
    setJsonText(text);
    try {
      const parsed = dashboardSpecSchema.safeParse(JSON.parse(text));
      if (!parsed.success) { setJsonError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(' · ')); return; }
      setJsonError(null);
      hist.set(parsed.data);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  if (item.isPending) return <Spinner />;
  if (!item.data) return null;
  const it = item.data.item;
  const published = it.status === 'published';
  const selected = spec?.widgets.find((w) => w.id === selectedId);
  const canSave = !!spec && !!datasetVersionId && !!validation?.success && !jsonError;

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-surface">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-white px-4">
        <div className="flex min-w-0 shrink-0 items-center gap-3 lg:max-w-[32%]">
          <Link to="/admin/rooms/$id" params={{ id: it.roomId }} className="eyebrow text-muted no-underline hover:text-foreground">Room</Link>
          <h1 className="hidden truncate text-[15px] font-[620] text-foreground lg:block">{spec ? pickLocale(spec.title, 'en') : pickLocale(it.title, 'en')}</h1>
          <Badge tone={published ? 'good' : 'neutral'}>{it.status}</Badge>
          {dirty ? <Tooltip content="Unsaved changes"><span className="size-2 rounded-full bg-warning" aria-label="Unsaved changes" /></Tooltip> : null}
        </div>
        <div className="mx-auto flex items-center gap-3">
          {spec ? <Segmented value={mode} onChange={setMode} ariaLabel="Mode" options={[{ value: 'edit', label: 'Edit' }, { value: 'preview', label: 'Preview' }, { value: 'json', label: 'JSON' }]} /> : null}
          {jsonError ? <span className="inline-flex items-center gap-1 text-[12.5px] text-critical"><AlertCircle className="size-3.5" />JSON invalid</span>
            : issues.length ? <Tooltip content={<ul className="list-disc pl-4">{issues.slice(0, 6).map((x, i) => <li key={i}>{x}</li>)}</ul>}><span className="inline-flex items-center gap-1 text-[12.5px] text-critical"><AlertCircle className="size-3.5" />{issues.length} issue{issues.length > 1 ? 's' : ''}</span></Tooltip>
            : preview.isError ? <span className="text-[12.5px] text-critical">{(preview.error as ApiError)?.reason ?? (preview.error as Error).message}</span>
            : render ? <span className="inline-flex items-center gap-1 text-[12.5px] text-good"><CheckCircle2 className="size-3.5" />valid{render.warnings.length ? ` · ${render.warnings.length} warning(s)` : ''}</span> : null}
          {preview.isFetching ? <RefreshCw className="size-3.5 animate-spin text-muted" /> : null}
        </div>
        <div className="flex items-center gap-1.5">
          <Tooltip content="Undo (⌘Z)"><Button variant="ghost" size="icon-sm" aria-label="Undo" disabled={!hist.canUndo} onClick={hist.undo}><Undo2 /></Button></Tooltip>
          <Tooltip content="Redo (⇧⌘Z)"><Button variant="ghost" size="icon-sm" aria-label="Redo" disabled={!hist.canRedo} onClick={hist.redo}><Redo2 /></Button></Tooltip>
          <Tooltip content="Save as template"><Button variant="ghost" size="icon-sm" aria-label="Save as template" disabled={!canSave} onClick={() => setTemplateOpen(true)}><LayoutTemplate /></Button></Tooltip>
          <Button variant="secondary" size="sm" onClick={() => save.mutate(false)} disabled={!canSave} loading={save.isPending && save.variables === false}><Save />Save draft</Button>
          <Button size="sm" onClick={() => save.mutate(true)} disabled={!canSave} loading={save.isPending && save.variables === true}><Upload />Save & publish</Button>
        </div>
      </header>

      {!datasetVersionId || !spec ? (
        <div className="flex-1 overflow-auto px-6 py-8">
          <BindDataset datasets={datasets.data?.datasets ?? []} templates={templates.data?.templates ?? []} onBind={(vid, f) => { setDatasetVersionId(vid); if (!spec) loadSpec(starterSpec(f.table, f.dateField, f.amountField, f.dimField)); }} onTemplate={(s, f) => loadSpec({ ...s, table: f.table, date_field: f.dateField })} />
        </div>
      ) : mode === 'preview' ? (
        <div className="min-h-0 flex-1 overflow-auto">
          {render ? (
            <React.Suspense fallback={<Spinner />}>
              <DashboardViewer render={render} controls={{ download: true, print: true, copy: true, watermark: false, watermark_downloads: false, blur_on_focus_loss: false }} drill={drill} mode="preview" onStateChange={setPreviewState} initialState={previewState} />
            </React.Suspense>
          ) : preview.isError ? <div className="p-8"><EmptyState title="Preview unavailable" body={(preview.error as ApiError)?.details ? JSON.stringify((preview.error as ApiError).details) : (preview.error as Error)?.message} /></div> : <Spinner />}
        </div>
      ) : mode === 'json' ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1fr_320px]">
          <Textarea value={jsonText} onChange={(e) => validateJson(e.target.value)} spellCheck={false} className="h-full min-h-0 resize-none rounded-none border-0 border-r border-border font-mono text-[12.5px] leading-5" />
          <aside className="overflow-auto bg-white p-4 text-[12.5px] text-muted">
            <p className="eyebrow mb-2">Spec v2</p>
            <p>Paste a spec authored elsewhere; it validates live and the canvas follows. v1 row layouts are migrated to grid cells automatically.</p>
            {jsonError ? <p className="mt-3 text-critical">{jsonError}</p> : issues.length ? <ul className="mt-3 list-disc pl-4 text-critical">{issues.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="mt-3 text-good">Valid.</p>}
          </aside>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[200px_minmax(0,1fr)_300px] overflow-hidden xl:grid-cols-[236px_minmax(0,1fr)_336px]">
          <aside className="min-h-0 overflow-y-auto border-r border-border bg-white">
            <div className="border-b border-border px-4 py-3">
              <p className="eyebrow mb-1.5">Dataset version</p>
              <Select value={datasetVersionId} onChange={(e) => setDatasetVersionId(e.target.value)} className="h-8 w-full text-[13px]">
                {(bound.data?.versions ?? []).map((v) => <option key={v.id} value={v.id} disabled={v.parseStatus !== 'ready'}>v{v.versionNo} · {v.filename}{v.parseStatus !== 'ready' ? ` (${v.parseStatus})` : ''}</option>)}
                {!bound.data ? <option value={datasetVersionId}>current</option> : null}
              </Select>
              {dataset ? <p className="mt-1 truncate text-[11.5px] text-muted">{dataset.name} · {version?.rowCount ?? '—'} rows</p> : null}
            </div>
            <div className="border-b border-border px-4 py-3">
              <p className="eyebrow mb-1.5">Add widget</p>
              {(['Numbers', 'Charts', 'Tables', 'Text'] as const).map((group) => (
                <div key={group} className="mb-2">
                  <p className="mb-1 text-[11px] text-muted">{group}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {PALETTE.filter((x) => WIDGET_META[x.type].group === group).map((x) => (
                      <Tooltip key={x.type} content={WIDGET_META[x.type].description}>
                        <button type="button" onClick={() => addWidget(x.type)} className="inline-flex h-8 items-center gap-1.5 rounded-btn border border-border px-2 text-left text-[12.5px] font-[520] text-body hover:bg-row-hover hover:text-foreground"><x.icon className="size-3.5 shrink-0 text-muted" /><span className="truncate">{WIDGET_META[x.type].label}</span></button>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3">
              <div className="mb-1.5 flex items-center justify-between"><p className="eyebrow">Widgets</p><span className="text-[11px] text-muted">{spec.widgets.length}</span></div>
              <ul className="space-y-0.5">
                {[...spec.layout].sort((a, b) => a.y - b.y || a.x - b.x).map((cell) => {
                  const w = spec.widgets.find((x) => x.id === cell.id);
                  if (!w) return null;
                  const label = w.title ? pickLocale(w.title, 'en') : 'text' in w ? pickLocale(w.text, 'en') : WIDGET_META[w.type].label;
                  return <li key={w.id}><button type="button" onClick={() => setSelectedId(w.id)} className={cn('flex w-full items-center gap-2 rounded-[6px] px-2 py-1 text-left text-[12.5px] hover:bg-row-hover', selectedId === w.id && 'bg-soft text-brand')}><span className="w-14 shrink-0 truncate text-[11px] text-muted">{WIDGET_META[w.type].label}</span><span className="truncate">{label || w.id}</span></button></li>;
                })}
              </ul>
            </div>
          </aside>
          <div className="min-h-0 overflow-auto">
            {spec.widgets.length === 0 ? (
              <div className="p-8"><EmptyState icon={PencilRuler} title="Empty dashboard" body="Add widgets from the palette on the left. Drag them by their handle, resize from the corner." action={{ label: 'Add a KPI', onClick: () => addWidget('kpi') }} /></div>
            ) : (
              <BuilderCanvas spec={spec} data={data} colors={colors} currency={dataset?.currency ?? spec.currency} selectedId={selectedId} onSelect={setSelectedId} onLayout={onLayout} onRemove={removeWidget} onDuplicate={duplicateWidget} locale="en" />
            )}
            {preview.isError ? <p className="px-8 pb-6 text-[12.5px] text-critical">{(preview.error as ApiError)?.details ? JSON.stringify((preview.error as ApiError).details) : (preview.error as Error)?.message}</p> : null}
          </div>
          <aside className="min-h-0 overflow-y-auto border-l border-border bg-white">
            {ctx ? <Inspector spec={spec} widget={selected} ctx={ctx} onSpec={onSpecPatch} onWidget={onWidget} onRemove={removeWidget} onDuplicate={duplicateWidget} /> : <Spinner />}
          </aside>
        </div>
      )}

      {item.data.versions.length > 0 ? (
        <div className="flex shrink-0 items-center gap-3 overflow-x-auto border-t border-border bg-white px-4 py-2">
          <span className="eyebrow shrink-0">Versions</span>
          {item.data.versions.map((v) => <span key={v.id} className="inline-flex shrink-0 items-center gap-1 text-[12px] text-muted">v{v.versionNo} <Badge tone={v.renderStatus === 'ready' ? 'good' : v.renderStatus === 'failed' ? 'critical' : 'warning'}>{v.renderStatus}</Badge>{v.publishedAt ? ' · published' : ''}</span>)}
        </div>
      ) : null}

      {templateOpen ? <SaveTemplateDialog onClose={() => setTemplateOpen(false)} onSave={(name) => saveTemplate.mutate(name)} busy={saveTemplate.isPending} defaultName={spec ? pickLocale(spec.title, 'en') : ''} /> : null}
    </div>
  );
}

function SaveTemplateDialog({ onClose, onSave, busy, defaultName }: { onClose: () => void; onSave: (name: string) => void; busy: boolean; defaultName: string }) {
  const [name, setName] = React.useState(defaultName);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Save as template" description="Templates start new dashboards on any dataset with the same columns." footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => onSave(name)} disabled={!name.trim()} loading={busy}>Save template</Button></>}>
      <Field label="Template name" htmlFor="tpl-name"><Input id="tpl-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} /></Field>
    </Dialog>
  );
}

function BindDataset({ datasets, onBind, templates, onTemplate }: { datasets: Array<{ id: string; name: string; latest_version?: DatasetVersion | null }>; onBind: (versionId: string, fields: FieldGuess) => void; templates: Array<{ id: string; name: string; description: string | null; spec: DashboardSpec }>; onTemplate: (s: DashboardSpec, fields: FieldGuess) => void }) {
  const [picked, setPicked] = React.useState<string>('');
  const ds = useDataset(picked);
  const [versionId, setVersionId] = React.useState('');
  const [table, setTable] = React.useState('');
  const [dateField, setDateField] = React.useState('');
  const [amountField, setAmountField] = React.useState('');
  const version = ds.data?.versions.find((v) => v.id === versionId) ?? ds.data?.versions[0];
  React.useEffect(() => { if (ds.data?.versions[0] && !versionId) setVersionId(ds.data.versions[0].id); }, [ds.data, versionId]);
  React.useEffect(() => {
    const t = version?.schema?.tables[0];
    if (t) {
      setTable(t.name);
      setDateField(t.columns.find((c) => c.type === 'date')?.name ?? '');
      setAmountField(t.columns.find((c) => c.type === 'currency' || c.type === 'number')?.name ?? '');
    }
  }, [version]);
  const cols = version?.schema?.tables.find((t) => t.name === table)?.columns ?? [];
  const fields = (): FieldGuess => ({ table, dateField, amountField, dimField: guessDim(cols, dateField) });
  if (datasets.length === 0) return <EmptyState title="No datasets yet" body="Create a dataset and upload a version first." action={{ label: 'Go to Datasets', onClick: () => { window.location.href = '/admin/datasets'; } }} />;
  return (
    <div className="mx-auto max-w-xl rounded-card border border-border bg-card p-6">
      <h2 className="section-title mb-1">Bind a dataset</h2>
      <p className="mb-4 text-[13px] text-muted">Pick the dataset version this dashboard reads from, then start from a template or a starter layout.</p>
      <Field label="Dataset"><Select value={picked} onChange={(e) => { setPicked(e.target.value); setVersionId(''); }}><option value="">Select…</option>{datasets.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select></Field>
      {ds.data ? (
        <>
          <Field label="Version"><Select value={versionId} onChange={(e) => setVersionId(e.target.value)}>{ds.data.versions.map((v) => <option key={v.id} value={v.id} disabled={v.parseStatus !== 'ready'}>v{v.versionNo} · {v.filename} {v.parseStatus !== 'ready' ? `(${v.parseStatus})` : ''}</option>)}</Select></Field>
          {version?.schema ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Table"><Select value={table} onChange={(e) => setTable(e.target.value)}>{version.schema.tables.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}</Select></Field>
              <Field label="Date column"><Select value={dateField} onChange={(e) => setDateField(e.target.value)}>{cols.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}</Select></Field>
              <Field label="Amount column"><Select value={amountField} onChange={(e) => setAmountField(e.target.value)}>{cols.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}</Select></Field>
            </div>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button disabled={!versionId || !table || !dateField} onClick={() => onBind(versionId, fields())}><Plus />Start with a starter layout</Button>
            {templates.map((t) => <Button key={t.id} variant="secondary" disabled={!versionId || !table || !dateField} onClick={() => { onBind(versionId, fields()); onTemplate(t.spec, fields()); }}><LayoutTemplate />{t.name}</Button>)}
          </div>
        </>
      ) : null}
    </div>
  );
}
